import { keysOverlap } from './touched.js';
/**
 * Why a change with no inverse has none, said in terms of what the user did.
 *
 * The server's own refusal names the command type and points at a point-in-time restore, which is honest
 * but says nothing about the decision behind it. Each of these is a handler that returns `inverse: null`
 * for a stated reason, and the reason is worth repeating where the button would have been — "restore from
 * a point in time" is a large, alarming action to offer for a reason the user cannot see.
 *
 * A per-TYPE sentence is accurate even for the handlers that only sometimes refuse: `setTargetLevels`
 * records no inverse exactly when it shortened the ladder, so if a `setTargetLevels` row has no inverse,
 * shortening is what happened.
 */
const IRREVERSIBLE = {
    applyDataUpdate: 'A roster refresh cannot be undone: putting the population back would mean deleting people, and a '
        + 'departure is a flag rather than a delete so that severance and the audit trail survive it.',
    /*
      Keyed by the COMMAND type, and this one was keyed by the name it nearly had.
  
      It read `rederiveRoster` — which the catalogue considered and did not adopt; the type is
      `rebuildRosterFromSource` (`packages/contracts/src/commands.ts`). So the lookup below missed on every
      real occurrence and the most destructive project-scoped command there is fell through to the generic
      fallback. Nothing caught it because the only fallback test used an invented type, which is a test that
      the fallback works rather than a test that nothing reaches it by accident. The coverage assertion in
      `core.test.ts` now derives the required keys from the registry's own lists instead.
    */
    rebuildRosterFromSource: 'Re-deriving the roster cannot be undone directly. It is a pure function of the retained source rows '
        + 'and the column mapping, so putting the mapping back and running it again reproduces the previous '
        + 'state exactly.',
    deleteScenario: 'Deleting a scenario cannot be undone: a whole design went with it — decisions, heads, overrides, '
        + 'TSAs and build-ups — and an undo that restored an empty shell would be worse than none.',
    resetRestructuring: 'Resetting the restructuring cannot be undone: it discarded every override and group rename for this '
        + 'perspective at once, rather than recording what each of them was.',
    removeTargetUnit: 'Removing a target unit cannot be undone: it took the branch beneath it and re-parented every '
        + 'placement that was inside it.',
    setTargetLevels: 'Shortening the target ladder cannot be undone: the levels below the new depth went with it.',
    /*
      The one entry whose remedy is the thing the other entries point AT, so it must not point back at
      itself in the same words. It says where the way out is instead: the point taken immediately before
      this restore, which exists by construction because `restoreProject` is in `NEVER_AN_INVERSE` and
      `executeBatch` snapshots before every command in that set.
    */
    restoreProject: 'A restore cannot be undone with an undo, because the design it replaced was not computed from the '
        + 'one before it. The way back is another restore: a point was taken immediately before this one.',
    renameTargetValue: 'That rename cannot be undone: it merged into a unit that already existed, and the two are now one.',
    reparentTargetUnit: 'That move cannot be undone: it merged into an existing unit, grew the ladder, or created parents on '
        + 'the way, so there is no single move back.',
    setDefaultScenario: 'There was no default scenario before this one, so there is nothing to put back — undoing it would '
        + 'leave the project opening on nothing.',
};
/*
  THE PROJECT, not the scenario, and the noun is the whole finding.

  Every one of these sentences used to end "Restore the scenario from a point in time instead", and so
  did the server's three refusals. That promise is unbacked either way — nothing implements a restore yet
  — but it named the wrong unit, which is worse than naming a missing one, because it is the sentence
  somebody will build against.

  A sequence number is a PROJECT fact. `change_events` is unique on `(project_id, seq)` and the seq is
  allocated by bumping `projects.seq` under the project row lock, while `scenario_id` is a nullable column
  with no foreign key, filled from the command's own schema. "Scenario X at seq N" is not a coordinate
  this log defines.

  And the object a user is looking at is not in a scenario. `loadSeparationState` draws exactly two of its
  fields from `loadScenarioDesign`; the roster, the assumptions every currency figure on screen comes from,
  the entities, the column mapping, the grouping levels and the whole target ladder are project-scoped.
  Rolling one scenario back against today's project state would produce a design that never existed.

  The rows this text actually appears on settle it. Of the handlers that can refuse an inverse, the
  destructive ones are mostly project-scoped — a roster refresh, a re-derivation, a shortened ladder, a
  removed target unit — so `scenarioName` is null, the row renders no scenario badge, and the old sentence
  offered to restore a scenario that the row does not name and does not have. `intake.ts` points at this
  very mechanism as the way back from a bad refresh, and a bad refresh replaces the project's roster.
*/
const IRREVERSIBLE_FALLBACK = 'This change cannot be undone. Restore the project from a point in time instead.';
/** "Ben Okri" when there is a name, "Someone" when there is not — the fallback every actor line uses. */
function who(culprit) {
    return culprit.actorName || 'Someone';
}
function blockedSentence(block, type, after) {
    switch (block) {
        case 'undone':
            /*
              NAMES THE PERSON AND THE CHANGE, which this alone did not.
      
              It is the one refusal that is by definition about somebody else's action, and it was the only one
              telling the reader neither who nor which. "Undo the revert further up" is also not dependable
              advice: with "Only my changes" ticked, the revert it points at is filtered out of the list being
              read, so the instruction names a row that is not there. A person and a number survive the filter.
            */
            return after === null
                ? 'Already undone. Undo the revert further up to put it back.'
                : `${who(after)} took this back in #${after.seq}. Undo that change to put this one back.`;
        case 'irreversible':
            return IRREVERSIBLE[type] ?? IRREVERSIBLE_FALLBACK;
        case 'behind-a-restore':
            /*
              NAMES THE PERSON NOW, revising a call P5ab made with less information.
      
              That entry argued the seq was enough here because "who did it is in the row at #after, which the
              reader can go and look at". The exit review then made the same point about the sibling sentence
              and it lands on both: with "Only my changes" ticked, the row this refers to is filtered out of the
              list being read, so "go and look at it" names something that is not on screen. The justification
              was the row's presence, and the row is not reliably present.
            */
            return ((after === null ? 'The project was restored' : `${who(after)} restored the project at #${after.seq}`)
                + ', so this change is from a design that has since been replaced. Restore the project from a '
                + 'point in time instead.');
        case 'nothing-to-undo':
            return 'Nothing to undo: this recorded something without changing the design.';
        case 'dead-scenario':
            // Kept in the log on purpose: deleting a scenario does not erase what was decided in it.
            return 'The scenario this changed has been deleted, so there is nothing left to undo it against.';
        case 'owner-only':
            return 'Undoing this needs the project owner: it would run a command only they may run.';
        case 'stale-destructive':
            /*
              Deliberately does NOT name the command it would run, though the server's own refusal does.
      
              `DESTRUCTIVE_INVERSES` is keyed by the INVERSE's command type — `deleteScenario`,
              `removeTargetUnit` — and the row's own type is the forward one, `duplicateScenario` or
              `addTargetUnit`. Looking the reason up by the row's type would silently find nothing on every row
              this fires for, and looking it up by the first member's type would name the wrong command on a
              mixed batch. The seq is the part a reader can act on, and it is exact.
            */
            return ('Undoing this would run something that cannot itself be undone, so it is offered only while it '
                + `is the most recent change${after === null ? '' : `, and ${who(after)} made #${after.seq} after it`}. `
                + 'Restore the project from a point in time instead.');
    }
}
/**
 * The order the server refuses in, most actionable first.
 *
 * `nothing-to-undo` sits last because it is the only one `groupBlock` requires of EVERY member: a batch
 * of one note and one real change has something to run. The others fire on any member.
 */
const PRECEDENCE = [
    'undone',
    'irreversible',
    // Third, matching `revert()`: the row's own properties, then the restore guard, then everything that
    // depends on other rows.
    'behind-a-restore',
    'dead-scenario',
    'owner-only',
    'stale-destructive',
    'nothing-to-undo',
];
const _everyBlockHasAPrecedence = true;
void _everyBlockHasAPrecedence;
/**
 * The block for a whole batch: its least revertable member, except that `nothing-to-undo` needs ALL of
 * them.
 *
 * That last part is the server's rule, not a softening of it. A batch's inverse is its members' inverses
 * concatenated, so one real change among a set of notes leaves something to run; the refusal only fires
 * when the concatenation is empty (apps/server/src/revert.ts).
 */
function groupBlock(members) {
    for (const block of PRECEDENCE) {
        if (block === 'nothing-to-undo') {
            if (members.every((m) => m.blocked === 'nothing-to-undo'))
                return block;
            continue;
        }
        if (members.some((m) => m.blocked === block))
            return block;
    }
    return null;
}
/**
 * The later change that will make this one's undo refuse — predicted, rather than discovered on click.
 *
 * The server's `assertNothingBuiltOnIt` refuses when any later event touched an overlapping key, and that
 * guard is deliberately NOT precomputed into `blocked`: it costs O(every later event) per row on the
 * server and races anything happening while the response is in flight. So a row could carry an Undo
 * button whose only possible outcome was a refusal, and the reader found out by pressing it.
 *
 * Here it is free and, on this page, EXACT. The list is newest-first, so every event later than a row is
 * ABOVE that row and therefore already in hand — no query, and nothing missing. `touched` is on the wire
 * as of P5f, and `keysOverlap` is the server's own comparison rather than a second one.
 *
 * TWO HONEST LIMITS. A batch that straddles the page boundary has members this page cannot see, so its
 * key set is incomplete and the prediction can miss — a false negative, which is the button that refuses,
 * which is exactly the behaviour without this. And the answer is a prediction: somebody can build on the
 * change between this response and the click. Both fail toward the server's refusal, which is the right
 * direction — the server is still the one that decides.
 *
 * Matched to the guard rather than improved on. It counts a later event even if that event has itself
 * been undone, because the server does; guessing differently here would produce a button the server then
 * refuses, which is the defect this fixes.
 */
function supersededBy(members, later) {
    const mine = members.flatMap((m) => m.touched ?? []);
    if (!mine.length)
        return null;
    /*
      Scoped by scenario the way the server scopes it: a key names a person, not a person in a scenario, so
      `bucket:E4` is written by a move in whichever design it happens in. A null scenario is PROJECT level
      and reaches everything — the target ladder, the roster and the assumptions are all like that.
    */
    const scenarios = new Set(members.map((m) => m.scenarioId));
    const projectWide = scenarios.has(null);
    const highest = Math.max(...members.map((m) => m.seq));
    for (const row of [...later].sort((a, b) => a.seq - b.seq)) {
        if (row.seq <= highest)
            continue;
        if (!(projectWide || row.scenarioId === null || scenarios.has(row.scenarioId)))
            continue;
        for (const theirs of row.touched ?? []) {
            if (mine.some((key) => keysOverlap(key, theirs))) {
                return { seq: row.seq, actorName: row.actorName };
            }
        }
    }
    return null;
}
/**
 * Group the change log into rows, newest first.
 *
 * Takes the payload in whatever order it arrives and sorts by seq itself, so the surface does not depend
 * on the endpoint's ordering — the ordering was wrong once already, and a client that quietly inherited
 * it would have shown the project's first afternoon forever.
 */
export function historyRows(events, options = {}) {
    const scenarioNames = new Map((options.scenarios ?? []).map((s) => [s.id, s.name]));
    // Grouped by batch, in first-seen order of the ASCENDING pass, so members stay in applied order.
    const groups = new Map();
    for (const event of [...events].sort((a, b) => a.seq - b.seq)) {
        const key = event.batchId ?? `seq:${event.seq}`;
        const bucket = groups.get(key);
        if (bucket)
            bucket.push(event);
        else
            groups.set(key, [event]);
    }
    const rows = [...groups].map(([key, members]) => {
        const head = members[0];
        const block = groupBlock(members);
        // Only asked when the server said nothing: "already undone" and "cannot be undone" are both better
        // answers than "somebody built on it", and the server's word takes precedence over this prediction.
        const superseded = block === null ? supersededBy(members, options.allEvents ?? events) : null;
        const size = Math.max(head.batchSize, members.length);
        return {
            key,
            seq: head.seq,
            size,
            members: members.map((m) => ({ seq: m.seq, summary: m.summary })),
            partial: size > members.length,
            summary: head.summary,
            actorName: head.actorName,
            createdAt: head.createdAt,
            scenarioName: head.scenarioId === null ? null : scenarioNames.get(head.scenarioId) ?? null,
            // A batch is undone as a unit, so any member being undone is the batch being undone.
            undone: members.some((m) => m.undone),
            canRevert: block === null && superseded === null,
            blockedReason: superseded !== null
                ? `${superseded.actorName || 'Someone'} has since made #${superseded.seq}, which builds on `
                    + 'this. Undo that one first, or restore the project to a point in time.'
                : block === null
                    ? ''
                    : blockedSentence(block, blockedType(members, block), blockedAfter(members, block, options.allEvents ?? events)),
            undoLabel: block !== null || superseded !== null
                ? ''
                : size === 1
                    ? 'Undo this change'
                    : `Undo all ${size} changes`,
            anchorEventId: head.id,
            /*
              `members` is already the ascending pass, so this is oldest-member-first without re-sorting.
      
              Truthiness rather than `=== null`, which is not pedantry here: the client does not re-validate
              the payload, so `note` is whatever the server sent. A response that omits the field entirely
              would spread `undefined` into a note object with a seq and no text — a quotation mark round
              nothing, on every row.
            */
            notes: members.flatMap((m) => (m.note ? [{ seq: m.seq, ...m.note }] : [])),
        };
    });
    // Newest first: the top row is the most recent thing that happened, which is what a change log is.
    return rows.sort((a, b) => b.seq - a.seq);
}
/**
 * Which member's type the sentence should be about.
 *
 * Only matters for `irreversible`, where the sentence is per command type: the batch is refused because
 * of a specific member, and naming a different one would explain the wrong thing.
 */
function blockedType(members, block) {
    return (members.find((m) => m.blocked === block) ?? members[0]).type;
}
/**
 * The seq the sentence names, chosen by which refusal is speaking.
 *
 * Two blocks carry a number and they are different numbers: `stale-destructive` names the later change
 * that took the undo away, `behind-a-restore` names the restore that replaced the design. A row can carry
 * both — the server fills each as a fact about the log rather than as a restatement of `blocked` — so
 * reading whichever happens to be non-null would print the wrong figure in the sentence roughly half the
 * time it mattered, and print it with total confidence.
 *
 * Any member of the batch carries it: the batch is blocked as a unit and both fields are computed against
 * the batch's last seq, so they agree across members that have them.
 */
function blockedAfter(members, block, everything) {
    /*
      `undone` carries its culprit on the row itself, because only the server can resolve it: the link is
      `change_events.undone_by`, an event id, and the reverting event may be off the page entirely.
    */
    if (block === 'undone')
        return members.find((m) => m.undoneBy != null)?.undoneBy ?? null;
    const seq = block === 'stale-destructive'
        ? members.find((m) => m.staleAfterSeq != null)?.staleAfterSeq ?? null
        : block === 'behind-a-restore'
            ? members.find((m) => m.restoredAtSeq != null)?.restoredAtSeq ?? null
            : null;
    if (seq === null)
        return null;
    /*
      Resolved against the UNFILTERED page, which is the whole reason `allEvents` is threaded through here.
      The blocking change is by definition somebody else's, so under "only my changes" it is exactly what
      the visible list does not contain — and a sentence that could only name it when it happened to be on
      screen would go quiet precisely when the reader most needs telling who to talk to.
    */
    return { seq, actorName: everything.find((e) => e.seq === seq)?.actorName ?? '' };
}
//# sourceMappingURL=history.js.map